import { Inject, Injectable } from '@angular/core';
import {
  DataTransmissionMode,
  ENV_CONFIG,
  EnvironmentConfig,
  NFIdentity,
  QueryOperation,
  ValueType
} from '@nibras/core/common';
import { NgForage } from 'ngforage';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  Observer,
  combineLatest,
  concat,
  forkJoin,
  from,
  of
} from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { AuthService } from './auth/auth.service';
import { CommonService } from './common.service';
import { CoreService } from './core.service';
import { FlaggedEnum } from './flagged-enum';
import { HttpService } from './http.service';
import { ComponentParameter } from './model/app.model';
import { dcDataStoreItem, dcElementDataStoreItem } from './model/datastore-item.model';
import { dcAttributeDataKey } from './model/dcAttributeDataKey.model';
import { dcAttributeTemplate } from './model/dcAttributeTemplate.model';
import { dcElement } from './model/dcElement.model';
import { dcElementTemplate } from './model/dcElementTemplate.model';
import { dcOverriddenQueryParams } from './model/dcOverriddenQueryParams.model';
import { dcTag } from './model/dcTag.model';
import { dcAttributeFilterTVP } from './model/dcattribute-filter-tvp.model';
import { dcAttribute } from './model/dcattribute.model';
import { dcAttributeValue } from './model/dcattributevalue.model';
import { dcDataReference } from './model/dcdataReference.model';
import { dcValue } from './model/dcvalue.model';
import { Dictionary, DictionaryNumber } from './model/dictionary';
import { GenericValue } from './model/generic-value.model';
import { AttributeSmartValue } from './model/request/attribute-smart-value.model';
import {
  AsyncRequestParams,
  AttributesCallback,
  BaseRequest,
  DataRequest,
  KeysCallback,
  LookupDataCallback,
  OnConsolidatedDataReceivedCallback,
  RequestType,
  ValuesCallback,
  dcExternalParameter,
  onComponentDataAsJSONReceivedCallback
} from './model/request/request.model';
import { AttributeAndValues } from './model/response/attribute-and-values.model';
import { ComponentData } from './model/response/component-data.model';
import { DataMethods } from './model/response/data-method.enum';
import { DataResponse } from './model/response/data-response.model';
import { JsonData } from './model/response/json-data.model';
import { RequestStatus, ResponseStatus } from './model/response/response-status.model';
import { dcSuggestedValue } from './model/suggested-value.model';
import { User } from './model/user.model';
import { ProcessActivity } from './model/workflow/process-Activity.model';
import { ProcessInstanceData } from './model/workflow/process-instance-data.model';
import { SharedCommonService } from './shared-common.service';
import { IToastService, TOAST_SERVICE } from './toast.interface';

enum DataStoreItemType {
  Element = 1,
  Attribute = 2,
  AttributeValue = 3,
  AttributeTemplate = 4,
  ElementTemplate = 5,
  UserProfile = 6,
  DataReference = 7
}

export type DataStoreItems = any;

@Injectable()
export class DataStoreService {
  private Data: DictionaryNumber<Observable<Dictionary<dcDataStoreItem>>>;
  private subjectDataArray: DictionaryNumber<BehaviorSubject<Dictionary<dcDataStoreItem>>> = {};
  private dataStoreDic: DictionaryNumber<Dictionary<dcDataStoreItem>>;
  private requestStatusCount = 0;
  public requestStatusList: Map<string, BehaviorSubject<RequestStatus>>;
  private lastCount = 0;
  private timeout: any;
  private bufferedDataKeys: dcAttributeDataKey[] = [];
  private bufferedDataValues: any = {};
  public t0: number;
  public t1: number;

  constructor(
    private httpService: HttpService,
    private coreService: CoreService,
    private readonly ngf: NgForage,
    private flaggedEnum: FlaggedEnum,
    private authService: AuthService,
    private commonService: CommonService,
    @Inject(TOAST_SERVICE) private toastService: IToastService,
    @Inject(ENV_CONFIG) private environmentConfig: EnvironmentConfig
  ) {
    this.initVaribles();
    this.initDataStoreDic<dcElementDataStoreItem>(DataStoreItemType.Element);
    this.initDataStoreDic<dcAttribute>(DataStoreItemType.Attribute);
    this.initDataStoreDic<dcAttributeValue>(DataStoreItemType.AttributeValue);
    this.initDataStoreDic<dcAttributeTemplate>(DataStoreItemType.AttributeTemplate);
    this.initDataStoreDic<dcElementTemplate>(DataStoreItemType.ElementTemplate);
    //this.initDataStoreDic<dcDataReference>(DataStoreItemType.DataReference);
    this.initDataStoreDic<User>(DataStoreItemType.UserProfile);
  }

  private initVaribles() {
    this.dataStoreDic = {};
    this.Data = {};
    this.requestStatusList = new Map();
  }

  private initDataStoreDic<T>(dataStoreItemType: DataStoreItemType) {
    this.dataStoreDic[dataStoreItemType] = {};

    this.subjectDataArray[dataStoreItemType] = this.getBehaviourDic<dcDataStoreItem>();
    this.Data[dataStoreItemType] = (
      this.subjectDataArray[dataStoreItemType] as BehaviorSubject<Dictionary<dcDataStoreItem>>
    ).asObservable();
  }

  private getBehaviourDic<T>() {
    return new BehaviorSubject({}) as BehaviorSubject<Dictionary<T>>;
  }

  /**
   * Subscribe for elements in datastore.
   */
  getElements(tags: dcTag[], includeAttributes = false): Observable<dcElement[]> {
    if (!tags || tags.length <= 0) {
      return EMPTY;
    }

    const requestType: RequestType = includeAttributes
      ? // eslint-disable-next-line no-bitwise
        RequestType.Element | RequestType.Attribute
      : RequestType.Element;
    return this.getElementDataStoreItem(tags, requestType) as Observable<dcElement[]>;
  }

  getProcessInstances(processInstanceIds: number[]): Observable<ProcessInstanceData[]> {
    if (!processInstanceIds || processInstanceIds.length <= 0) {
      return EMPTY;
    }

    const requestType: RequestType = RequestType.ProcessInstance;
    const tags = processInstanceIds.map(a => new dcTag(0, '', NFIdentity.ProcessDesign, 0, a));

    return this.getElementDataStoreItem(tags, requestType) as Observable<ProcessInstanceData[]>;
  }

  getTaskProcessInstances(): Observable<ProcessInstanceData[]> {
    // if (!processInstanceIds || processInstanceIds.length <= 0) return Observable.empty();
    const processInstanceIds: number[] = [];
    processInstanceIds.push(1);
    const requestType: RequestType = RequestType.ProcessInstance;
    const tags = processInstanceIds.map(a => new dcTag(0, '', NFIdentity.ProcessDesign, 0, a));

    return this.getElementDataStoreItemKeyProcess(tags, requestType) as Observable<
      ProcessInstanceData[]
    >;
  }

  loadDataStoreWithProcessInstances() {
    const requestStatus = this.generateRequestStatus();
    const requestID = this.generateRequestIDByStatus(requestStatus);
    const tags: dcTag[] = [];
    const tag = new dcTag();
    tag.ElementTypeID = NFIdentity.ProcessDesign;
    tags.push(tag);

    const dataRequest = new DataRequest(requestID, tags, RequestType.ProcessInstance, null, null);

    const requestCollection = this.populateRequest(tags, dataRequest);

    return this.sendRequest(requestCollection).pipe(
      map(processInstanceDataResponse => {
        const newDataStoreItem = this.dataStoreDic[DataStoreItemType.Element];

        const processes: Array<ProcessInstanceData> = [];
        for (const i in processInstanceDataResponse.Collection) {
          const processElement = processInstanceDataResponse.Collection[i] as ProcessInstanceData;
          this.save(
            newDataStoreItem,
            processElement,
            processElement.Key,
            DataStoreItemType.Element
          );
          processes.push(processElement);
        }
        this.publishInMemoryList(newDataStoreItem, DataStoreItemType.Element);
        return processes;
      })
    );
  }

  getAllDatareferences() {
    const requestStatus = this.generateRequestStatus();
    const requestID = this.generateRequestIDByStatus(requestStatus);
    const fakeTags: dcTag[] = [new dcTag()];
    const dataRequest = new DataRequest(requestID, fakeTags, RequestType.DataReference);
    dataRequest.UserSessionID = this.authService.userSessionID;

    const dataStoreItemType = this.mapDataStoreItemType(dataRequest.RequestType);

    //This is a async/parallel activity to get the data from InMemory Array or Client DB or API (in specified order)
    this.generalProcessAndMakeAPIRequest(dataRequest, dataStoreItemType);
    return requestStatus.requestStatus;
  }

  getAllDatareferenceById(queryManagerId: number) {
    const requestStatus = this.generateRequestStatus();
    const requestID = this.generateRequestIDByStatus(requestStatus);
    const dataRefTag: dcTag[] = [
      new dcTag(0, '', NFIdentity.Element, queryManagerId, queryManagerId)
    ];
    const dataRequest = new DataRequest(requestID, dataRefTag, RequestType.DataReference);
    dataRequest.UserSessionID = this.authService.userSessionID;

    const dataStoreItemType = this.mapDataStoreItemType(dataRequest.RequestType);

    //This is a async/parallel activity to get the data from InMemory Array or Client DB or API (in specified order)
    this.generalProcessAndMakeAPIRequest(dataRequest, dataStoreItemType);
    return requestStatus.requestStatus;
  }

  getDatareferenceById(queryManagerId: number): Observable<dcDataReference> {
    return this.httpService.get<dcDataReference>(
      'Element/GetDatareferenceById?queryManagerId=' + queryManagerId
    );
  }

  private generalProcessAndMakeAPIRequest(
    dataRequest: DataRequest,
    dataStoreItemType: DataStoreItemType
  ) {
    const keysNotExist: Observable<dcTag | dcDataStoreItem>[] = this.getKeysNotExistsExt(
      dataRequest,
      null,
      dataStoreItemType,
      false
    );

    const requestStatusSubject = this.requestStatusList.get(dataRequest.RequestID);

    forkJoin(keysNotExist)
      .pipe(
        withLatestFrom(tagsAndData => {
          const tagNotExists = tagsAndData.filter(
            tags => !(tags as any).hasOwnProperty('Key')
          ) as dcTag[];
          const existingDataStoreItems = tagsAndData.filter(tags =>
            (tags as any).hasOwnProperty('Key')
          ) as dcDataStoreItem[];

          if (existingDataStoreItems && existingDataStoreItems.length > 0) {
            this.setNewResponseStatus(
              requestStatusSubject,
              ResponseStatus.ExistingDataAdded,
              existingDataStoreItems
            );
          }

          const requestCollection = this.populateRequest(tagNotExists, dataRequest);
          if (!requestCollection || (requestCollection && requestCollection.length < 1)) {
            return null;
          }
          this.setNewResponseStatus(requestStatusSubject, ResponseStatus.Pending);
          return this.sendRequest(requestCollection, dataStoreItemType);
        }),
        filter(a => a != null),
        mergeMap(a => a)
      )
      .subscribe(dataResponse => {
        if (!dataResponse) return;
        this.setNewResponseStatus(requestStatusSubject, ResponseStatus.SavingInDB);
        const dataStoreItems: dcDataStoreItem[] = [];
        for (const dataStoreItemKey in dataResponse.Collection) {
          const dataStoreItem = dataResponse.Collection[dataStoreItemKey] as dcDataStoreItem;

          if (!this.flaggedEnum.hasFlag(dataRequest.RequestType, RequestType.AttributeValue)) {
            this.setItemInLocalDB(dataStoreItemKey, dataStoreItem, dataStoreItemType);
          }
          dataStoreItems.push(dataStoreItem);
        }

        this.setNewResponseStatus(
          requestStatusSubject,
          ResponseStatus.FullDataReceived,
          dataStoreItems
        );
        this.setNewResponseStatus(requestStatusSubject, ResponseStatus.Done);
      });
  }

  findAttribute(attributes: dcAttribute[], attributeName: string) {
    return this.commonService.findAttribute(attributes, attributeName) as dcAttribute;
  }

  findAttributeById(attributes: dcAttribute[], attributeVersionId: number) {
    return attributes.find(attribute => attribute.Header.AttributeTemplateID == attributeVersionId);
  }
  /**
   * Subscribe for any object which are parsed from dcElement. For App, ProcessInstance, Element etc
   */
  getElementDataStoreItem(
    tags: dcTag[],
    requestType: RequestType = RequestType.Element
  ): Observable<dcElementDataStoreItem[]> {
    if (!tags || tags.length <= 0) {
      return EMPTY;
    }

    const requestID = this.generateRequestID();
    const dataRequest = new DataRequest(requestID, tags, requestType);
    return this.getDataStoreItemByDataRequest(dataRequest) as Observable<dcElementDataStoreItem[]>;
  }

  getElementDataStoreItemKeyProcess(
    tags: dcTag[],
    requestType: RequestType = RequestType.Element
  ): Observable<dcElementDataStoreItem[]> {
    if (!tags || tags.length <= 0) {
      return EMPTY;
    }

    const requestID = this.generateRequestID();
    const dataRequest = new DataRequest(requestID, tags, requestType);
    return this.getDataStoreItemKeyProcessInstance(dataRequest) as Observable<
      dcElementDataStoreItem[]
    >;
  }

  private generateRequestStatus(responseStatus: ResponseStatus = ResponseStatus.New) {
    this.requestStatusCount++;
    const requestID = `${this.requestStatusCount + 1}_${this.authService.userSessionID.replace(
      new RegExp('-', 'g'),
      ''
    )}`;
    return {
      requestID,
      requestStatus: new BehaviorSubject(new RequestStatus(requestID, responseStatus))
    };
  }

  private generateRequestID() {
    const requestStatus = this.generateRequestStatus();
    return this.generateRequestIDByStatus(requestStatus);
  }

  private generateRequestIDByStatus(requestStatus: {
    requestID: string;
    requestStatus: BehaviorSubject<RequestStatus>;
  }) {
    this.requestStatusList.set(requestStatus.requestID, requestStatus.requestStatus);
    return requestStatus.requestID;
  }

  private getDataStoreItemByDataRequest(
    dataRequest: DataRequest,
    sendApiRequest = true
  ): Observable<dcDataStoreItem[]> {
    const dataStoreItemType = this.mapDataStoreItemType(dataRequest.RequestType);

    const originalTags = Object.assign([], dataRequest.Tags) as dcTag[];
    originalTags.forEach(tag => (tag.dataStoreKey = this.getDataStoreKey(tag, dataStoreItemType)));
    //This is a async/parallel activity to get the data from InMemory Array or Client DB or API (in specified order)
    this.processAndMakeAPIRequest(dataRequest, dataStoreItemType, sendApiRequest);

    return this.Data[dataStoreItemType].pipe(
      map(dataStoreItems =>
        this.filterDataStoreItems(
          originalTags,
          dataStoreItems,
          dataRequest.ExternalParameter,
          dataStoreItemType
        )
      )
    );
  }

  private getDataStoreItemKeyProcessInstance(
    dataRequest: DataRequest,
    sendApiRequest = false
  ): Observable<dcDataStoreItem[]> {
    const dataStoreItemType = this.mapDataStoreItemType(dataRequest.RequestType);

    const originalTags = Object.assign([], dataRequest.Tags) as dcTag[];
    originalTags.forEach(tag => (tag.dataStoreKey = this.getDataStoreKey(tag, dataStoreItemType)));
    //This is a async/parallel activity to get the data from InMemory Array or Client DB or API (in specified order)
    this.processAndMakeAPIRequest(dataRequest, dataStoreItemType, sendApiRequest);

    const processDataStoreList: dcDataStoreItem[] = [];
    return this.Data[dataStoreItemType].pipe(
      map((elementList: any) => {
        for (const list in elementList) {
          const item = elementList[list];
          if (item.ElementTypeID !== undefined && item.ElementTypeID == NFIdentity.ProcessDesign) {
            const existingItemIndex = processDataStoreList.findIndex(
              listItem => listItem.Key === item.Key
            );
            if (existingItemIndex > -1) processDataStoreList[existingItemIndex] = item;
            else processDataStoreList.push(item);
          }
        }
        return processDataStoreList;
      })
    );
  }

  private getAttributesWithValues(
    attributeFilters: Array<dcAttributeFilterTVP> = [],
    externalParameter?: dcExternalParameter,
    maxItems: number = 500
  ): AttributeAndValues {
    const attributeTags = this.attributeFilterTVPToTag(attributeFilters);

    if (!attributeTags || attributeTags.length <= 0) return null;

    const requestID = this.generateRequestID();
    const dataRequest = new DataRequest(
      requestID,
      attributeTags,
      // eslint-disable-next-line no-bitwise
      RequestType.Attribute | RequestType.AttributeValue,
      attributeFilters,
      externalParameter
    );
    dataRequest.UserSessionID = this.authService.userSessionID;
    return this.getAttributesWithValuesByDataRequest(dataRequest);
  }

  getAttributeValues(
    attributeFilters: Array<dcAttributeFilterTVP> = [],
    externalParameter?: dcExternalParameter
  ) {
    const attributeTags = this.attributeFilterTVPToTag(attributeFilters);

    if (!attributeTags || attributeTags.length <= 0) return null;

    const requestStatus = this.generateRequestStatus();
    const requestID = this.generateRequestIDByStatus(requestStatus);
    const dataRequest = new DataRequest(
      requestID,
      attributeTags,
      RequestType.AttributeValue,
      attributeFilters,
      externalParameter
    );
    dataRequest.UserSessionID = this.authService.userSessionID;

    //return this.getDataStoreItemByDataRequest(dataRequest) as Observable<dcAttributeValue[]>;
    const dataStoreItemType = this.mapDataStoreItemType(dataRequest.RequestType);

    //This is a async/parallel activity to get the data from InMemory Array or Client DB or API (in specified order)
    this.attributeValueProcessAndMakeAPIRequest(dataRequest, dataStoreItemType);
    return requestStatus.requestStatus;
  }

  private attributeFilterTVPToTag(attributeFilters: dcAttributeFilterTVP[]) {
    return attributeFilters.map(attributeFilter => {
      const tag = new dcTag(0, '', NFIdentity.Attribute, 0);
      tag.dataStoreKey = this.generateDataStoreKey(
        attributeFilter.ElementVersionID,
        attributeFilter.ElementTypeID,
        attributeFilter.AttributeTemplateVersionID
      );
      return tag;
    });
  }

  clearInMemoryList(dataStoreItemType: DataStoreItemType) {}

  private generateDataStoreKey(
    elementVersionID: number,
    elementTypeID: number,
    attributeTemplateVersionID: number
  ): string {
    return `${elementVersionID}_${elementTypeID}_${attributeTemplateVersionID}`;
  }

  private generateDataStoreKeyByFilter(attributeFilter: dcAttributeFilterTVP): string {
    return this.generateDataStoreKey(
      attributeFilter.ElementVersionID,
      attributeFilter.ElementTypeID,
      attributeFilter.AttributeTemplateVersionID
    );
  }

  private generateDataStoreKeyByAttr(attr: dcAttribute): string {
    return this.generateDataStoreKey(
      attr.Header.ElementVersionID,
      attr.Header.ElementTypeID,
      attr.Header.AttributeTemplateID
    );
  }
  private generateDataStoreKeyByAttributeValue(attr: dcAttributeValue): string {
    return this.generateDataStoreKey(
      attr.ElementVersionID,
      attr.ElementTypeID,
      attr.AttributeTemplateVersionID
    );
  }
  private getAttributesWithValuesByDataRequest(dataRequest: DataRequest): AttributeAndValues {
    const attributes = this.getDataStoreItemByDataRequest(dataRequest) as Observable<dcAttribute[]>;
    const attributeValuesReq = Object.assign({}, dataRequest);
    //attributeValuesReq.RequestID = this.generateRequestID();
    attributeValuesReq.RequestType = RequestType.AttributeValue;
    const attributeValues = this.getDataStoreItemByDataRequest(
      attributeValuesReq,
      false
    ) as Observable<dcAttributeValue[]>;
    return new AttributeAndValues(attributes, attributeValues, attributeValuesReq.RequestID);
  }

  getAttributesFromApi(
    attributeFilters: Array<dcAttributeFilterTVP> = [],
    externalParameter?: dcExternalParameter
  ): AttributeAndValues {
    const fakeTags: dcTag[] = [new dcTag()];
    const requestStatus = this.generateRequestStatus();
    const requestID = this.generateRequestIDByStatus(requestStatus);

    const dataRequest = new DataRequest(
      requestID,
      fakeTags,
      RequestType.Attribute,
      attributeFilters,
      externalParameter
    );
    dataRequest.UserSessionID = this.authService.userSessionID;

    const requestCollection = this.populateRequest(fakeTags, dataRequest);
    if (!requestCollection || (requestCollection && requestCollection.length < 1)) {
      //this.requestStatusList.delete(dataRequest.RequestID);
      return null;
    }
    const $attributes = this.sendRequest(requestCollection, DataStoreItemType.Attribute).pipe(
      map(dataResponse => {
        if (!dataResponse) return;
        //this.setNewResponseStatus(requestStatus.requestStatus, ResponseStatus.SavingInDB);
        const attributes: dcAttribute[] = [];
        for (const dataStoreItemKey in dataResponse.Collection) {
          const dataStoreItem = dataResponse.Collection[dataStoreItemKey] as dcElement;
          dataStoreItem.Attributes.forEach(attribute => attributes.push(attribute));
        }
        return attributes;
      })
    );
    return new AttributeAndValues($attributes, null, requestID);
  }

  getDataAsync(
    tags: dcTag[],
    externalParameter: dcExternalParameter,
    rootElementVersionID: number,
    elementVersionID: number, //WidgetID
    thisArg: any,
    processAttributesCallback: AttributesCallback,
    processKeysCallback: KeysCallback,
    processValuesCallback: ValuesCallback,
    processOnConsolidatedDataReceivedCallback: OnConsolidatedDataReceivedCallback,
    processonComponentDataAsJSONReceivedCallback: onComponentDataAsJSONReceivedCallback,
    storedAttributeDataKeyList: dcAttributeDataKey[] = [],
    componentParameter: ComponentParameter[] = [],
    dataTransmissionMode: DataTransmissionMode = null,
    overriddenQueryParams?: Array<dcOverriddenQueryParams>
  ) {
    //This condition is in case of Refresh requests.
    if (this.isRefreshRequest(storedAttributeDataKeyList)) {
      return this.refreshDataAsync(
        storedAttributeDataKeyList,
        thisArg,
        externalParameter,
        processAttributesCallback,
        processKeysCallback,
        processValuesCallback,
        processOnConsolidatedDataReceivedCallback,
        processonComponentDataAsJSONReceivedCallback,
        componentParameter,
        dataTransmissionMode,
        tags
      );
    }
    //Normal first request (Typically for all components in an app)
    return this._getDataAsync(
      tags,
      externalParameter,
      rootElementVersionID,
      elementVersionID, //WidgetID
      // null,
      null,
      thisArg,
      processAttributesCallback,
      processKeysCallback,
      processValuesCallback,
      processOnConsolidatedDataReceivedCallback,
      processonComponentDataAsJSONReceivedCallback,
      null,
      null,
      null,
      overriddenQueryParams
    );
  }

  getDataByAttributesAsync(
    attributes: dcAttribute[] = [],
    //  externalParameterList: dcExternalParameter[] = [],
    externalParameter: dcExternalParameter,
    thisArg: any,
    processAttributesCallback: AttributesCallback,
    processKeysCallback: KeysCallback,
    processValuesCallback: ValuesCallback,
    processOnConsolidatedDataReceivedCallback: OnConsolidatedDataReceivedCallback,
    processonComponentDataAsJSONReceivedCallback: onComponentDataAsJSONReceivedCallback
  ) {
    const fakeTags: dcTag[] = [new dcTag()];
    return this._getDataAsync(
      fakeTags,
      externalParameter,
      0,
      0,
      attributes,
      // externalParameterList,
      thisArg,
      processAttributesCallback,
      processKeysCallback,
      processValuesCallback,
      processOnConsolidatedDataReceivedCallback,
      processonComponentDataAsJSONReceivedCallback
    );
  }

  getDataByAttributesAsyncexternalParam(
    attributes: dcAttribute[] = [],
    externalParameter: dcExternalParameter,
    thisArg: any,
    processAttributesCallback: AttributesCallback,
    processKeysCallback: KeysCallback,
    processValuesCallback: ValuesCallback,
    processOnConsolidatedDataReceivedCallback: OnConsolidatedDataReceivedCallback,
    processonComponentDataAsJSONReceivedCallback: onComponentDataAsJSONReceivedCallback
  ) {
    const fakeTags: dcTag[] = [new dcTag()];
    return this._getDataAsync(
      fakeTags,
      externalParameter,
      0,
      0,
      attributes,
      // null,
      thisArg,
      processAttributesCallback,
      processKeysCallback,
      processValuesCallback,
      processOnConsolidatedDataReceivedCallback,
      processonComponentDataAsJSONReceivedCallback
    );
  }

  private _getDataAsync(
    tags: dcTag[],
    externalParameter: dcExternalParameter,
    rootElementVersionID: number,
    elementVersionID: number, //WidgetID
    attributes: dcAttribute[],
    //externalParameterList: dcExternalParameter[],
    thisArg: any,
    processAttributesCallback: AttributesCallback,
    processKeysCallback: KeysCallback,
    processValuesCallback: ValuesCallback,
    processOnConsolidatedDataReceivedCallback: OnConsolidatedDataReceivedCallback,
    processonComponentDataAsJSONReceivedCallback: onComponentDataAsJSONReceivedCallback,
    attributeDataKeyList: dcAttributeDataKey[] = null,
    componentParameter: ComponentParameter[] = [],
    dataTransmissionMode: DataTransmissionMode = null,
    overriddenQueryParams?: Array<dcOverriddenQueryParams>
  ) {
    const attributesAndRequestID = this._getAttributeAndValuesAsync(
      tags,
      externalParameter,
      rootElementVersionID,
      elementVersionID, //WidgetID
      attributes,
      // externalParameterList,
      attributeDataKeyList,
      componentParameter,
      dataTransmissionMode,
      null,
      null,
      null,
      null,
      overriddenQueryParams
    );
    attributesAndRequestID.Attributes.subscribe(() => {
      console.log('Attributes received');
    });
    this.handleAsyncResponses(
      attributesAndRequestID.RequestID,
      thisArg,
      processAttributesCallback,
      processKeysCallback,
      processValuesCallback,
      processOnConsolidatedDataReceivedCallback,
      processonComponentDataAsJSONReceivedCallback,
      null
    );
    return attributesAndRequestID.RequestID;
  }

  triggerAsyncRequest(asyncDataRequest: AsyncRequestParams) {
    const attributesAndRequestID = this._getAttributeAndValuesAsync(
      asyncDataRequest.tags,
      asyncDataRequest.externalParameter,
      asyncDataRequest.rootElementVersionID,
      asyncDataRequest.elementVersionID, //WidgetID
      asyncDataRequest.attributes,
      asyncDataRequest.attributeDataKeyList,
      asyncDataRequest.componentParameter,
      asyncDataRequest.dataTransmissionMode,
      asyncDataRequest.processInstanceID,
      asyncDataRequest.activityInstanceID,
      asyncDataRequest.parentProcessInstanceID,
      asyncDataRequest.attributeFilter
    );
    attributesAndRequestID.Attributes.subscribe(() => {
      console.log('Attributes received');
    });
    this.handleAsyncResponses(
      attributesAndRequestID.RequestID,
      asyncDataRequest.thisArg,
      asyncDataRequest.processAttributesCallback,
      asyncDataRequest.processKeysCallback,
      asyncDataRequest.processValuesCallback,
      asyncDataRequest.processOnConsolidatedDataReceivedCallback,
      asyncDataRequest.processonComponentDataAsJSONReceivedCallback,
      asyncDataRequest.lookupDataReceivedCallback
    );
    return attributesAndRequestID.RequestID;
  }

  public refreshDataAsync(
    attributeDataKeyList: dcAttributeDataKey[],
    thisArg: any,
    externalParameter: dcExternalParameter,
    processAttributesCallback: AttributesCallback,
    processKeysCallback: KeysCallback,
    processValuesCallback: ValuesCallback,
    processOnConsolidatedDataReceivedCallback: OnConsolidatedDataReceivedCallback,
    processonComponentDataAsJSONReceivedCallback: onComponentDataAsJSONReceivedCallback,
    componentParameter: ComponentParameter[] = [],
    dataTransmissionMode: DataTransmissionMode = null,
    tags: dcTag[]
  ) {
    return this._getDataAsync(
      tags,
      externalParameter,
      0,
      0,
      null,
      // null,
      thisArg,
      processAttributesCallback,
      processKeysCallback,
      processValuesCallback,
      processOnConsolidatedDataReceivedCallback,
      processonComponentDataAsJSONReceivedCallback,
      attributeDataKeyList,
      componentParameter,
      dataTransmissionMode
    );
  }

  public handleAsyncResponses(
    requestID: string,
    thisArg: any,
    processAttributesCallback: AttributesCallback,
    processKeysCallback: KeysCallback,
    processValuesCallback: ValuesCallback,
    processOnConsolidatedDataReceivedCallback: OnConsolidatedDataReceivedCallback,
    processonComponentDataAsJSONReceivedCallback: onComponentDataAsJSONReceivedCallback,
    lookupDataCallback: LookupDataCallback,
    dataRebindTimeout: number = 0
  ) {
    if (!requestID) return;
    let attributeList: dcAttribute[] = [];
    let attributeDataKeyList: dcAttributeDataKey[] = [];
    let isAttributesReceived = false;
    let isKeysReceived = false;
    let isValuesReceived = false;
    const bufferedAttributeValues: DataStoreItems = {};
    let bufferedJSONValue: DataStoreItems;
    let bufferedConsolidatedValue: DataStoreItems;

    this.requestStatusList.get(requestID).subscribe(requestStatus => {
      if (requestStatus.ResponseStatus == ResponseStatus.AttributesReceived) {
        attributeList = this.processAttributesReceived(
          requestStatus,
          processAttributesCallback,
          thisArg
        );
        isAttributesReceived = true;
      } else if (requestStatus.ResponseStatus == ResponseStatus.KeysReceived) {
        attributeDataKeyList = this.processKeysReceived(
          requestStatus,
          processKeysCallback,
          thisArg
        );
        isKeysReceived = true;

        if (bufferedConsolidatedValue) {
          this.processConsolidatedDataReceived(
            processOnConsolidatedDataReceivedCallback,
            thisArg,
            bufferedConsolidatedValue,
            attributeDataKeyList
          );
        } else if (bufferedJSONValue) {
          this.processComponentDataAsJSONReceived(
            processonComponentDataAsJSONReceivedCallback,
            thisArg,
            bufferedJSONValue,
            attributeDataKeyList
          );
        } else if (isValuesReceived) {
          this.processDataReceived(
            bufferedAttributeValues,
            attributeDataKeyList,
            processValuesCallback,
            thisArg,
            attributeList,
            dataRebindTimeout
          );
        }
      } else if (requestStatus.ResponseStatus == ResponseStatus.FullDataReceived) {
        if (isKeysReceived) {
          this.processDataReceived(
            requestStatus.DataStoreItems,
            attributeDataKeyList,
            processValuesCallback,
            thisArg,
            attributeList,
            dataRebindTimeout
          );
        } else {
          this.mergeValuesReceived(requestStatus, bufferedAttributeValues);
        }
        isValuesReceived = true;
      } else if (requestStatus.ResponseStatus == ResponseStatus.ConsolidatedDataReceived) {
        if (isKeysReceived) {
          this.processConsolidatedDataReceived(
            processOnConsolidatedDataReceivedCallback,
            thisArg,
            requestStatus.DataStoreItems,
            attributeDataKeyList
          );
        } else {
          const componentData = requestStatus.DataStoreItems as ComponentData[];
          bufferedConsolidatedValue = componentData;
        }
      } else if (requestStatus.ResponseStatus == ResponseStatus.onComponentDataAsJSONReceived) {
        if (isKeysReceived) {
          this.processComponentDataAsJSONReceived(
            processonComponentDataAsJSONReceivedCallback,
            thisArg,
            requestStatus.DataStoreItems,
            attributeDataKeyList
          );
        } else {
          const componentData = requestStatus.DataStoreItems as JsonData[];
          bufferedJSONValue = componentData;
        }
      } else if (requestStatus.ResponseStatus == ResponseStatus.LookupDataReceived) {
        this.processLookupDataReceived(
          requestStatus,
          attributeDataKeyList,
          lookupDataCallback,
          thisArg
        );
      }
    });
  }

  private processComponentDataAsJSONReceived(
    processonComponentDataAsJSONReceivedCallback: onComponentDataAsJSONReceivedCallback,
    thisArg: any,
    dataStoreItems: DataStoreItems,
    attributeDataKeyList: dcAttributeDataKey[]
  ) {
    if (processonComponentDataAsJSONReceivedCallback) {
      const componentData = dataStoreItems as Dictionary<GenericValue>[];
      processonComponentDataAsJSONReceivedCallback.call(
        thisArg,
        componentData,
        attributeDataKeyList
      );
    }
  }

  private processLookupDataReceived(
    requestStatus: RequestStatus,
    attributeDataKeyList: dcAttributeDataKey[],
    lookupDataRecivedCallBack: LookupDataCallback,
    thisArg: any
  ) {
    if (lookupDataRecivedCallBack) {
      const componentData = requestStatus.DataStoreItems as any as Dictionary<dcSuggestedValue[]>;
      lookupDataRecivedCallBack.call(thisArg, attributeDataKeyList, componentData);
    }
  }

  private processConsolidatedDataReceived(
    processOnConsolidatedDataReceivedCallback: OnConsolidatedDataReceivedCallback,
    thisArg: any,
    dataStoreItems: DataStoreItems,
    attributeDataKeyList: dcAttributeDataKey[]
  ) {
    if (processOnConsolidatedDataReceivedCallback) {
      const componentData = dataStoreItems as ComponentData[];
      processOnConsolidatedDataReceivedCallback.call(thisArg, componentData, attributeDataKeyList);
    }
  }

  private mergeValuesReceived(
    requestStatus: RequestStatus,
    bufferedAttributeValues: DataStoreItems
  ) {
    const values = requestStatus.DataStoreItems as DataStoreItems;
    for (const key in values) {
      if (!bufferedAttributeValues[key]) {
        bufferedAttributeValues[key] = values[key];
      }
    }
  }

  private processAttributesReceived(
    requestStatus: RequestStatus,
    processAttributesCallback: AttributesCallback,
    thisArg: any
  ) {
    //console.log('attr:' + requestStatus.RequestID, requestStatus);

    const attributeList = requestStatus.DataStoreItems as dcAttribute[];
    if (processAttributesCallback) processAttributesCallback.call(thisArg, attributeList);
    return attributeList;
  }

  private processKeysReceived(
    requestStatus: RequestStatus,
    processKeysCallback: KeysCallback,
    thisArg: any
  ) {
    //console.log('keys:' + requestStatus.RequestID, requestStatus);
    const attributeDataKeyList = requestStatus.DataStoreItems as dcAttributeDataKey[];
    if (processKeysCallback) {
      processKeysCallback.call(thisArg, attributeDataKeyList);
    }
    return attributeDataKeyList;
  }

  private processDataReceived(
    dataStoreItems: DataStoreItems,
    attributeDataKeyList: dcAttributeDataKey[],
    processValuesCallback: ValuesCallback,
    thisArg: any,
    attributeList: dcAttribute[],
    dataRebindTimeout: number
  ) {
    //console.log('data:' + requestStatus.RequestID, requestStatus);

    const attributeValueList: Dictionary<dcValue[]> = {};
    const attributeValues = dataStoreItems; //TODO: need to change this any[] to static
    if (attributeValues) {
      if (processValuesCallback && attributeDataKeyList.length > 0) {
        if (this.lastCount == 0 || dataRebindTimeout <= 0) {
          for (const key in attributeValues) {
            if (!attributeValueList[key]) {
              attributeValueList[key] = attributeValues[key];
            }
          }
          this.lastCount++;
          this.invokeProcessValuesCallback(
            processValuesCallback,
            thisArg,
            attributeList,
            attributeDataKeyList,
            attributeValueList
          );
        } else {
          clearInterval(this.timeout);
          this.timeout = setTimeout(() => {
            console.log('timeout');
            this.invokeProcessValuesCallback(
              processValuesCallback,
              thisArg,
              attributeList,
              this.bufferedDataKeys,
              this.bufferedDataValues
            );
            this.lastCount = 1;
            this.bufferedDataKeys = [];
            this.bufferedDataValues = [];
          }, dataRebindTimeout);
          this.lastCount++;
          attributeDataKeyList.forEach(dataKey => this.bufferedDataKeys.push(dataKey));
          for (const key in attributeValues) {
            if (!this.bufferedDataValues[key]) {
              this.bufferedDataValues[key] = attributeValues[key];
            }
          }
        }
      }
    }
  }

  private invokeProcessValuesCallback(
    processValuesCallback: ValuesCallback,
    thisArg: any,
    attributeList: dcAttribute[],
    attributeDataKeyList: dcAttributeDataKey[],
    attributeValueList: Dictionary<dcValue[]>
  ) {
    //console.log('invoking ', this.lastCount);
    processValuesCallback.call(thisArg, attributeList, attributeDataKeyList, attributeValueList);
  }

  getAttributeAndValuesAsync(
    tags: dcTag[],
    externalParameter: dcExternalParameter,
    rootElementVersionID: number,
    elementVersionID: number, //WidgetID
    attributeDataKeys: dcAttributeDataKey[] = null,
    widgetComponentParameters: ComponentParameter[] = null,
    dataTransmissionMode: DataTransmissionMode = null,
    processInstanceID?: number,
    activityInstanceID?: number,
    parentProcessInstanceID?: number,
    overriddenQueryParams?: Array<dcOverriddenQueryParams>,
    triggerDefaultRequest = false
  ) {
    if ((!tags || tags.length <= 0) && !triggerDefaultRequest) {
      tags = [new dcTag()];
    } else if (triggerDefaultRequest) {
      tags = [];
    }

    return this._getAttributeAndValuesAsync(
      tags,
      externalParameter,
      rootElementVersionID,
      elementVersionID, //WidgetID
      // null,
      null,
      attributeDataKeys,
      widgetComponentParameters,
      dataTransmissionMode,
      processInstanceID,
      activityInstanceID,
      parentProcessInstanceID,
      null,
      overriddenQueryParams,
      triggerDefaultRequest
    );
  }

  private _getAttributeAndValuesAsync(
    tags: dcTag[],
    externalParameter?: dcExternalParameter,
    rootElementVersionID?: number,
    elementVersionID?: number, //WidgetID
    attributes: dcAttribute[] = [],
    // externalParameterList: dcExternalParameter[] = [],
    storedAttributeDataKeys: dcAttributeDataKey[] = null,
    widgetComponentParameters: ComponentParameter[] = null,
    dataTransmissionMode: DataTransmissionMode = null,
    processInstanceID?: number,
    activityInstanceID?: number,
    parentProcessInstanceID?: number,
    attributeFilter?: dcAttributeFilterTVP[],
    overriddenQueryParams?: Array<dcOverriddenQueryParams>,
    triggerDefaultRequest = false
  ): AttributeAndValues {
    if (this.isRefreshRequest(storedAttributeDataKeys) && externalParameter != null) {
      this.setExternalParameterInDataKeys(storedAttributeDataKeys, externalParameter);
    }

    const requestStatus = this.generateRequestStatus(ResponseStatus.PendingDataFromApi);
    const requestID = this.generateRequestIDByStatus(requestStatus);
    const requestType =
      overriddenQueryParams && overriddenQueryParams.length
        ? RequestType.AdhocTrending
        : RequestType.AttributeAndValuesAsync;
    const dataRequest = new DataRequest(
      requestID,
      tags,
      requestType,
      null,
      externalParameter,
      rootElementVersionID,
      elementVersionID,
      [],
      activityInstanceID,
      processInstanceID,
      parentProcessInstanceID,
      null,
      null,
      overriddenQueryParams
    );
    dataRequest.UserSessionID = this.authService.userSessionID;
    dataRequest.Attributes = attributes;
    dataRequest.AttributeFilter = attributeFilter;
    // dataRequest.ExternalParameters = externalParameterList;
    dataRequest.AttributeDataKeys = storedAttributeDataKeys;
    dataRequest.DataTransmissionMode = dataTransmissionMode;

    if (widgetComponentParameters && widgetComponentParameters.length > 0) {
      dataRequest.ComponentParameters = widgetComponentParameters;
    }

    const requestCollection = this.populateRequest(tags, dataRequest, triggerDefaultRequest);

    const $attributes = this.sendRequest(requestCollection, DataStoreItemType.AttributeValue).pipe(
      map(dataReponse => {
        if (dataReponse && dataReponse.Collection && dataReponse.Collection[0]) {
          const element = dataReponse.Collection[0] as dcElement;
          return element as any as dcAttribute[];
        }
      })
    );
    return new AttributeAndValues($attributes, null, requestID);
  }

  private setExternalParameterInDataKeys(
    storedAttributeDataKeys: dcAttributeDataKey[],
    externalParameter: dcExternalParameter
  ) {
    storedAttributeDataKeys.forEach(dataKey => {
      if (
        dataKey.ExternalParameter &&
        dataKey.ExternalParameter.DataMethod != DataMethods.Snapshot
      ) {
        if (externalParameter.StartTime && externalParameter.EndTime) {
          dataKey.ExternalParameter.StartTime = SharedCommonService.parseDateForApi(
            externalParameter.StartTime
          );
          dataKey.ExternalParameter.EndTime = SharedCommonService.parseDateForApi(
            externalParameter.EndTime
          );
        }
      }
      //This to to reduce the load to server.
      //Api was not sending parameters since there are too much to data(test with Fahud to recreate this issue);
      //dataKey.attribute = null;
    });
  }

  private isRefreshRequest(storedAttributeDataKeys: dcAttributeDataKey[]) {
    return storedAttributeDataKeys && storedAttributeDataKeys.length > 0;
  }
  getAttributeValuesFromLocalDB(
    attributeFilters: Array<dcAttributeFilterTVP> = [],
    externalParameter?: dcExternalParameter
  ) {
    /*const sss = attributeFilters.map(attributeFilter => {
      return from(this.getItemFromLocalDB(dataStoreKey, DataStoreItemType.AttributeValue));
    });*/
    const dddd = attributeFilters.map(attributeFilter => {
      const dataStoreKey = this.generateDataStoreKeyByFilter(attributeFilter);
      const ss = new Observable((observer: Observer<dcDataStoreItem>) => {
        this.getItemFromLocalDB(dataStoreKey, DataStoreItemType.AttributeValue).then(
          data => {
            if (data) observer.next(data);
          },
          reason => {
            //observer.error(reason);
          }
        );
      });
      return ss;
    });

    const values = combineLatest(dddd).pipe(filter(attr => attr != null));
    return values as Observable<dcAttributeValue[]>;

    /*const vv = Observable.create((observer1: Observer<dcDataStoreItem[]>) => {
      attributeFilters.map(attributeFilter => {
        const dataStoreKey = this.generateDataStoreKeyByFilter(attributeFilter);
        const ss = Observable.create((observer: Observer<dcDataStoreItem>) => {
          this.getItemFromLocalDB(dataStoreKey, DataStoreItemType.AttributeValue).then(
            data => {
              if (data) observer.next(data);
            },
            reason => {
              //observer.error(reason);
            }
          );
        });
        return ss;
      });
    });
    return vv as Observable<dcAttributeValue[]>;*/

    //const values = Observable.merge([], sss);
    //return values as Observable<dcAttributeValue[]>;
  }

  //getElement(elementVersionId: number) { }

  sendRequest(requestCollection: DataRequest[], dataStoreItemType?: DataStoreItemType) {
    const requestName = this.environmentConfig.production
      ? ''
      : '?requestType=' + DataStoreItemType[dataStoreItemType];

    const cancelToken = requestCollection
      ?.map(request =>
        request.RootElementVersionID && request.ElementVersionID
          ? `${request.RootElementVersionID}_${request.ElementVersionID}_${request.ExternalParameter?.DataMethod}`
          : null
      )
      .join(',');

    const mainDataCall = this.httpService.post<DataResponse>(
      'tags/ProcessRequests' + requestName,
      requestCollection,
      cancelToken
    );

    const reconnectCall = this.coreService.reconnectSingalRIfNotConnected();
    const response = concat(
      reconnectCall.pipe(
        map(connection =>
          requestCollection.forEach(request => (request.UserSessionID = connection.id))
        )
      ),
      mainDataCall
    ).pipe(map(res => res as DataResponse));

    return response;
  }

  requestForAttributes(requestCollection: BaseRequest[]) {
    return this.httpService.post<DataResponse>('tags/GetAttributes', requestCollection);
  }

  requestForElementWithAttributeValues(elementVersionId: number) {
    return this.httpService.get<dcElement>(
      `Element/GetElementWithAttributeValues/${elementVersionId}`
    );
  }

  private filterDataStoreItems(
    tags: dcTag[],
    dataStoreItems: Dictionary<dcDataStoreItem>,
    externalParameter?: dcExternalParameter,
    dataStoreItemType?: DataStoreItemType
  ) {
    const filteredDataStoreItems: dcDataStoreItem[] = [];
    tags.forEach(tag => {
      const element = dataStoreItems[tag.dataStoreKey];
      if (element) {
        if (externalParameter) {
          const attributeValue = Object.assign({}, element) as dcAttributeValue;
          const plotValuesObjectKey = DataMethods[DataMethods.PlotValues];
          const attributeValueData = attributeValue ? (attributeValue.Data as any) : null;
          if (
            attributeValueData &&
            attributeValueData[plotValuesObjectKey] &&
            attributeValueData[plotValuesObjectKey].length > 0
          ) {
            const plotValues = attributeValueData[plotValuesObjectKey];
            const dateFilteredPlotValues = plotValues.filter((plotValue: dcValue) => {
              const timeStamp = new Date(plotValue.TimeStamp);
              return this.between(
                timeStamp,
                externalParameter.StartTime,
                externalParameter.EndTime
              );
            });
            attributeValueData[plotValuesObjectKey] = dateFilteredPlotValues;
          }
        }
        if (dataStoreItemType == DataStoreItemType.Element) {
          (element as dcElement).Tags = [tag];
        }
        filteredDataStoreItems.push(element);
      }
    });
    return filteredDataStoreItems;
  }

  between(input: Date, startDate: Date, endDate: Date): boolean {
    return input >= startDate && input < endDate;
  }

  private processAndMakeAPIRequest(
    dataRequest: DataRequest,
    dataStoreItemType: DataStoreItemType,
    sendApiRequest = true
  ) {
    const newDataStoreItem = this.dataStoreDic[dataStoreItemType];
    const keysNotExist: Observable<dcTag>[] = this.getKeysNotExists(
      dataRequest,
      newDataStoreItem,
      dataStoreItemType
    );

    if (!sendApiRequest) return;
    const requestStatusSubject = this.requestStatusList.get(dataRequest.RequestID);

    forkJoin(keysNotExist)
      .pipe(
        switchMap(tags => {
          const requestCollection = this.populateRequest(tags, dataRequest);
          if (!requestCollection || (requestCollection && requestCollection.length < 1)) {
            //this.requestStatusList.delete(dataRequest.RequestID);
            return [null];
          }
          this.setNewResponseStatus(requestStatusSubject, ResponseStatus.Pending);
          return this.sendRequest(requestCollection, dataStoreItemType);
        })
      )
      .subscribe(dataResponse => {
        if (!dataResponse) return;
        this.setNewResponseStatus(requestStatusSubject, ResponseStatus.SavingInDB);

        for (const dataStoreItemKey in dataResponse.Collection) {
          const dataStoreItem = dataResponse.Collection[dataStoreItemKey] as dcElement;

          if (this.flaggedEnum.hasFlag(dataRequest.RequestType, RequestType.Element)) {
            this.save(newDataStoreItem, dataStoreItem, dataStoreItemKey, dataStoreItemType);
          }

          if (
            this.flaggedEnum.hasFlag(dataRequest.RequestType, RequestType.Attribute) &&
            dataStoreItem.Attributes &&
            dataStoreItem.Attributes.length > 0
          ) {
            const newDataStoreItemAttribute = this.dataStoreDic[DataStoreItemType.Attribute];

            dataStoreItem.Attributes.forEach(attribute =>
              this.save(
                newDataStoreItemAttribute,
                attribute,
                this.generateDataStoreKey(
                  attribute.Header.ElementVersionID,
                  attribute.Header.ElementTypeID,
                  attribute.Header.AttributeTemplateID
                ),
                DataStoreItemType.Attribute
              )
            );
            this.publishInMemoryList(newDataStoreItemAttribute, DataStoreItemType.Attribute);
          }

          if (
            this.flaggedEnum.hasFlag(dataRequest.RequestType, RequestType.AttributeValue) &&
            dataStoreItem.AttributeValues &&
            dataStoreItem.AttributeValues.length > 0
          ) {
            const newDataStoreItemAttributeValue =
              this.dataStoreDic[DataStoreItemType.AttributeValue];

            dataStoreItem.AttributeValues.forEach(attributeValue =>
              this.save(
                newDataStoreItemAttributeValue,
                attributeValue,
                this.generateDataStoreKey(
                  attributeValue.ElementVersionID,
                  attributeValue.ElementTypeID,
                  attributeValue.AttributeTemplateVersionID
                ),
                DataStoreItemType.AttributeValue
              )
            );
            this.publishInMemoryList(
              newDataStoreItemAttributeValue,
              DataStoreItemType.AttributeValue
            );

            this.setNewResponseStatus(requestStatusSubject, ResponseStatus.FullDataReceived);
          }
        }
        this.publishInMemoryList(newDataStoreItem, dataStoreItemType);

        if (this.flaggedEnum.hasFlag(dataRequest.RequestType, RequestType.AttributeValue)) {
          this.setNewResponseStatus(requestStatusSubject, ResponseStatus.PendingDataFromApi);
        } else {
          //this.requestStatusList.delete(dataRequest.RequestID);
          this.setNewResponseStatus(requestStatusSubject, ResponseStatus.Done);
        }
      });
  }

  private attributeValueProcessAndMakeAPIRequest(
    dataRequest: DataRequest,
    dataStoreItemType: DataStoreItemType,
    sendApiRequest = true
  ) {
    const newDataStoreItem = this.dataStoreDic[dataStoreItemType];
    const keysNotExist: Observable<dcAttributeFilterTVP | dcAttributeValue>[] =
      this.attributeValueKeysNotExists(dataRequest, newDataStoreItem, dataStoreItemType);
    if (!sendApiRequest) return;
    const requestStatusSubject = this.requestStatusList.get(dataRequest.RequestID);

    forkJoin(keysNotExist)
      .pipe(
        withLatestFrom(attributeFilters => {
          const newAttributeFilters = attributeFilters.filter(
            attributeFilter => !(attributeFilter as any).hasOwnProperty('Key')
          ) as dcAttributeFilterTVP[];
          const existingDataStoreItems = attributeFilters.filter(attributeFilter =>
            (attributeFilter as any).hasOwnProperty('Key')
          ) as dcAttributeValue[];
          if (existingDataStoreItems && existingDataStoreItems.length > 0) {
            this.setNewResponseStatus(
              requestStatusSubject,
              ResponseStatus.ExistingDataAdded,
              existingDataStoreItems
            );
          }

          const fakeTags: dcTag[] = [new dcTag()];
          dataRequest.AttributeFilter = newAttributeFilters;
          const requestCollection = this.populateRequest(fakeTags, dataRequest);
          if (
            !requestCollection ||
            (requestCollection && requestCollection.length < 1) ||
            !dataRequest.AttributeFilter ||
            (dataRequest.AttributeFilter && dataRequest.AttributeFilter.length < 1)
          ) {
            //this.requestStatusList.delete(dataRequest.RequestID);
            return null;
          }
          this.setNewResponseStatus(requestStatusSubject, ResponseStatus.Pending);
          return this.sendRequest(requestCollection, dataStoreItemType);
        }),
        filter(a => a != null),
        mergeMap(a => a)
      )
      .subscribe(dataResponse => {
        if (!dataResponse) return;
        this.setNewResponseStatus(requestStatusSubject, ResponseStatus.SavingInDB);

        for (const dataStoreItemKey in dataResponse.Collection) {
          const dataStoreItem = dataResponse.Collection[dataStoreItemKey] as dcElement;

          if (
            this.flaggedEnum.hasFlag(dataRequest.RequestType, RequestType.AttributeValue) &&
            dataStoreItem.AttributeValues &&
            dataStoreItem.AttributeValues.length > 0
          ) {
            const newDataStoreItemAttributeValue =
              this.dataStoreDic[DataStoreItemType.AttributeValue];

            dataStoreItem.AttributeValues.forEach(attributeValue =>
              this.save(
                newDataStoreItemAttributeValue,
                attributeValue,
                this.generateDataStoreKey(
                  attributeValue.ElementVersionID,
                  attributeValue.ElementTypeID,
                  attributeValue.AttributeTemplateVersionID
                ),
                DataStoreItemType.AttributeValue,
                true
              )
            );

            this.setNewResponseStatus(
              requestStatusSubject,
              ResponseStatus.FullDataReceived,
              dataStoreItem.AttributeValues
            );
          }
        }
        this.setNewResponseStatus(requestStatusSubject, ResponseStatus.Done);
      });
  }

  private setNewResponseStatus(
    requestStatusSubject: BehaviorSubject<RequestStatus>,
    responseStatus: ResponseStatus,
    dataStoreItems?: dcDataStoreItem[]
  ) {
    const newStatus = { ...{}, ...requestStatusSubject.value };
    newStatus.ResponseStatus = responseStatus;
    if (responseStatus == ResponseStatus.FullDataReceived) {
      newStatus.Count = requestStatusSubject.value.Count + 1;
    }

    if (dataStoreItems && Array.isArray(dataStoreItems)) {
      if (requestStatusSubject.value.ResponseStatus != responseStatus) {
        newStatus.DataStoreItems = [];
      }
      if (dataStoreItems && dataStoreItems.length > 0) {
        if (newStatus.DataStoreItems && newStatus.DataStoreItems.length > 0) {
          dataStoreItems.forEach(dataStoreItem => newStatus.DataStoreItems.push(dataStoreItem));
        } else {
          newStatus.DataStoreItems = dataStoreItems;
        }
      }
    } else if (dataStoreItems) {
      const dataDic = dataStoreItems as any;
      if (requestStatusSubject.value.ResponseStatus != responseStatus) {
        (newStatus.DataStoreItems as any) = {};
      }
      let newStatusDataStoreItems = newStatus.DataStoreItems as any;
      if (newStatusDataStoreItems) {
        for (const key in dataDic) {
          if (!newStatusDataStoreItems[key]) {
            newStatusDataStoreItems[key] = dataDic[key];
          }
        }
      } else {
        newStatusDataStoreItems = dataDic;
      }
      //newStatus.DataStoreItems = newStatusDataStoreItems;
    }

    requestStatusSubject.next(newStatus);
  }

  private save(
    inMemoryArray: Dictionary<dcDataStoreItem>,
    dataStoreItem: dcDataStoreItem,
    dataStoreKey: string,
    dataStoreItemType: DataStoreItemType,
    skipInMemory = false
  ) {
    if (dataStoreItemType == DataStoreItemType.AttributeValue) {
      const attributeValue = dataStoreItem as dcAttributeValue;
      const plotValuesObjectKey = DataMethods[DataMethods.PlotValues];
      const attributeValueData = attributeValue ? (attributeValue.Data as any) : null;

      if (
        attributeValueData &&
        attributeValueData[plotValuesObjectKey] &&
        attributeValueData[plotValuesObjectKey].length > 0
      ) {
        const plotValues = attributeValueData[plotValuesObjectKey];
        const externalParameter = new dcExternalParameter({});
        externalParameter.StartTime = new Date(plotValues[0].TimeStamp);
        externalParameter.EndTime = new Date(plotValues[plotValues.length - 1].TimeStamp);
        if (!attributeValue.DataReaderParam) {
          attributeValue.DataReaderParam[0].Params = new dcExternalParameter();
        }
        (attributeValue.DataReaderParam[0].Params as any)[plotValuesObjectKey] = externalParameter;
      }
    }

    if (inMemoryArray && !skipInMemory)
      this.setItemInInMemoryArray(inMemoryArray, dataStoreItem, dataStoreItemType);

    //Store in localDB
    //TODO: condition check keysNotExist.filter(tag=>tag.TagID== )
    this.setItemInLocalDB(dataStoreKey, dataStoreItem, dataStoreItemType);
  }

  public saveAttributeValueInLocalDB(
    requestID: string,
    attributeValues: dcDataStoreItem[],
    responseDataMethod: DataMethods,
    responseStatus: ResponseStatus = ResponseStatus.FullDataReceived
  ) {
    const requestStatusSubject = this.requestStatusList.get(requestID);
    if (requestStatusSubject) {
      requestStatusSubject.pipe(take(1)).subscribe(requestStatus => {
        if (!requestStatus) {
          return;
        }
        // TODO: Refactor API to avoid using exceptions for no-data cases
        //  - Create a new response event to handle such cases explicitly
        //  - Implement proper status codes to differentiate response types
        // Note:
        //  - The current logic only handles the specific case where the SP returns 'No Element(s) found'
        //  - Multiple framework APIs may require similar handling, so relying on status messages is not reliable
        if (responseStatus === ResponseStatus.ExceptionReceived) {
          const exceptionResponse = attributeValues as any as string;
          // Show info toast only if it's a known "no elements" response
          if (exceptionResponse === 'No Element(s) found') {
            this.toastService.openInfoToast(exceptionResponse);
          } else {
            // Show generic error toast for all other exception responses
            this.toastService.openErrorToast(exceptionResponse);
          }
          if (requestStatus.ResponseStatus != ResponseStatus.KeysReceived) {
            this.updateAttributeValueInLocalDB(
              [],
              responseDataMethod,
              requestStatusSubject,
              ResponseStatus.KeysReceived
            );
          }
        } else if (
          requestStatus.ResponseStatus === ResponseStatus.PendingDataFromApi ||
          requestStatus.ResponseStatus === ResponseStatus.FullDataReceived ||
          requestStatus.ResponseStatus === ResponseStatus.KeysReceived ||
          requestStatus.ResponseStatus === ResponseStatus.AttributesReceived ||
          requestStatus.ResponseStatus === ResponseStatus.ConsolidatedDataReceived ||
          requestStatus.ResponseStatus === ResponseStatus.onComponentDataAsJSONReceived ||
          requestStatus.ResponseStatus === ResponseStatus.LookupDataReceived
        ) {
          this.updateAttributeValueInLocalDB(
            attributeValues,
            responseDataMethod,
            requestStatusSubject,
            responseStatus
          );
        }
      });
    } else {
      this.updateAttributeValueInLocalDB(attributeValues, responseDataMethod);
    }
  }

  private updateAttributeValueInLocalDB(
    attributeValues: dcDataStoreItem[],
    responseDataMethod: DataMethods,
    requestStatusSubject?: BehaviorSubject<RequestStatus>,
    responseStatus: ResponseStatus = ResponseStatus.FullDataReceived
  ) {
    if (responseDataMethod == DataMethods.Snapshot) {
      //Don't delete this code. This is required later.
      /*attributeValues.forEach(attributeValue => {
        const dataStoreKey = this.generateDataStoreKeyByAttr(attributeValue);
        this.getItemFromLocalDB(dataStoreKey, DataStoreItemType.AttributeValue).then(
          (localAttributeValue: dcAttributeValue) => {
            if (localAttributeValue && localAttributeValue.Data) {
              localAttributeValue.Data[DataMethods[responseDataMethod]] =
                attributeValue.Data[DataMethods[responseDataMethod]];
              attributeValue = localAttributeValue;
            }
            this.setItemInLocalDB(dataStoreKey, attributeValue, DataStoreItemType.AttributeValue);
          }
        );
      });*/
    }
    if (requestStatusSubject) {
      this.setNewResponseStatus(requestStatusSubject, responseStatus, attributeValues);
    }
  }

  private getKeysNotExists(
    dataRequest: DataRequest,
    newDataStoreItem: Dictionary<dcDataStoreItem>,
    dataStoreItemType: DataStoreItemType
  ) {
    return this.getKeysNotExistsExt(
      dataRequest,
      newDataStoreItem,
      dataStoreItemType
    ) as Observable<dcTag>[];
  }

  private getKeysNotExistsExt(
    dataRequest: DataRequest,
    newDataStoreItem: Dictionary<dcDataStoreItem>,
    dataStoreItemType: DataStoreItemType,
    publish = true
  ) {
    return dataRequest.Tags.map(tag => {
      if (tag) {
        //If marked for GetLatest from API, no need to check in InMemoryArray or in ClientDB.
        if (tag.dataStoreOptions && tag.dataStoreOptions.getLastest) {
          return of(tag);
        }

        //TODO: Remove this if condition, temp added
        if (
          dataStoreItemType == DataStoreItemType.AttributeValue ||
          dataStoreItemType == DataStoreItemType.Attribute ||
          !tag.dataStoreKey
        ) {
          return of(tag);
        }
        if (!(newDataStoreItem && newDataStoreItem[tag.dataStoreKey])) {
          const promise: Observable<dcTag | dcDataStoreItem> = from(
            this.getItemFromLocalDB(tag.dataStoreKey, dataStoreItemType)
          ).pipe(
            map((localDBElement: dcDataStoreItem) => {
              if (!localDBElement) {
                return tag;
              }
              if (publish) {
                this.setItemInInMemoryArray(
                  newDataStoreItem,
                  localDBElement,
                  dataStoreItemType,
                  true
                );
              } else {
                return localDBElement as dcDataStoreItem;
              }
            })
          );
          return promise;
        }
      }
    }).filter(a => a != null);
  }

  private attributeValueKeysNotExists(
    dataRequest: DataRequest,
    newDataStoreItem: Dictionary<dcDataStoreItem>,
    dataStoreItemType: DataStoreItemType
  ) {
    return dataRequest.AttributeFilter.map(attributeFilter => {
      //If marked for GetLatest from API, no need to check in InMemoryArray or in ClientDB.
      /* if (attributeFilter.dataStoreOptions && attributeFilter.dataStoreOptions.getLastest) {
        return Observable.of(attributeFilter);
      } */
      const key = this.generateDataStoreKeyByFilter(attributeFilter);
      //const attributeValue = newDataStoreItem[key] as dcAttributeValue;
      //if (!attributeValue) {
      const promise: Observable<dcAttributeFilterTVP | dcAttributeValue> = from(
        this.getItemFromLocalDB(key, dataStoreItemType)
      ).pipe(
        map((localDBElement: any) => {
          if (!localDBElement) {
            return attributeFilter;
          }

          if (dataRequest.ExternalParameter) {
            const attributeValue = Object.assign({}, localDBElement) as dcAttributeValue;
            const plotValuesObjectKey = DataMethods[DataMethods.PlotValues];
            const attributeValueDataReaderParams = attributeValue.DataReaderParam[0].Params as any;
            const timeStamp = attributeValueDataReaderParams[plotValuesObjectKey].StartTime;
            const valueExternalParam = attributeValueDataReaderParams[plotValuesObjectKey];
            /* if (
            this.between(
              dataRequest.ExternalParameter.StartTime,
              valueExternalParam.StartTime,
              valueExternalParam.EndTime
            ) &&
            this.between(
              dataRequest.ExternalParameter.EndTime,
              valueExternalParam.StartTime,
              valueExternalParam.EndTime
            )
          ) {
            if (
              attributeValue.Data &&
              attributeValue.Data[plotValuesObjectKey] &&
              attributeValue.Data[plotValuesObjectKey].length > 0
            ) {
              const plotValues = attributeValue.Data[plotValuesObjectKey];
              const dateFilteredPlotValues = plotValues.filter((plotValue: dcValue) => {
                const timeStamp = new Date(plotValue.TimeStamp);
                return this.between(
                  timeStamp,
                  dataRequest.ExternalParameter.StartTime,
                  dataRequest.ExternalParameter.EndTime
                );
              });
              //attributeValue.Data[plotValuesObjectKey] = dateFilteredPlotValues;
              if (dateFilteredPlotValues.length > 0) {
              }
              attributeValue.Data[plotValuesObjectKey] = dateFilteredPlotValues;
              return attributeValue;
            }
          } else {
            if (
              attributeValue.Data &&
              attributeValue.Data[plotValuesObjectKey] &&
              attributeValue.Data[plotValuesObjectKey].length > 0
            ) {
              const plotValues = attributeValue.Data[plotValuesObjectKey];
              const dateFilteredPlotValues = plotValues.filter((plotValue: dcValue) => {
                const timeStamp = new Date(plotValue.TimeStamp);
                return this.between(
                  timeStamp,
                  dataRequest.ExternalParameter.StartTime,
                  dataRequest.ExternalParameter.EndTime
                );
              });
              //attributeValue.Data[plotValuesObjectKey] = dateFilteredPlotValues;
              if (dateFilteredPlotValues.length > 0) {
              }
            }
            return;
          } */
          }

          return localDBElement;
        })
      );
      return promise;
      //}
    }).filter(a => a != null);
  }

  private setItemInInMemoryArray(
    newDataStoreItem: Dictionary<dcDataStoreItem>,
    storeItem: dcDataStoreItem,
    dataStoreItemType: DataStoreItemType,
    publish = false
  ) {
    switch (dataStoreItemType) {
      case DataStoreItemType.Element: {
        const dcElementItem = storeItem as dcElementDataStoreItem;

        if (dcElementItem && dcElementItem.Key && dcElementItem.Key.trim() != '')
          newDataStoreItem[(storeItem as dcElementDataStoreItem).Key] = storeItem;
        else newDataStoreItem[(storeItem as dcElement).Header.ElementVersionID] = storeItem;
        break;
      }
      case DataStoreItemType.Attribute: {
        const attribute = storeItem as dcAttribute;
        const dataStoreKey = this.generateDataStoreKeyByAttr(attribute);
        newDataStoreItem[dataStoreKey] = storeItem;
        break;
      }
      case DataStoreItemType.AttributeValue: {
        const attributeValue = { ...{}, ...storeItem } as dcAttributeValue;
        //const attributeValue = cloneDeep(storeItem) as dcAttributeValue;
        const dataStoreKey = this.generateDataStoreKeyByAttributeValue(attributeValue);
        let data = null;
        const plotValuesObjectKey = DataMethods[DataMethods.PlotValues];
        const attributeValueData = attributeValue ? (attributeValue.Data as any) : null;
        if (attributeValueData && attributeValueData[plotValuesObjectKey]) {
          data = Object.assign({}, attributeValueData);
          //delete attributeValue.Data[plotValuesObjectKey];
        }

        newDataStoreItem[dataStoreKey] = attributeValue;

        if (data) {
          //(storeItem as dcAttributeValue).Data = data;
        }
        break;
      }
      case DataStoreItemType.AttributeTemplate: {
        newDataStoreItem[(storeItem as dcAttributeTemplate).Header.ElementTemplateID] = storeItem;
        break;
      }
      case DataStoreItemType.ElementTemplate: {
        newDataStoreItem[(storeItem as dcElementTemplate).Header.ElementTemplateID] = storeItem;
        break;
      }
      case DataStoreItemType.UserProfile: {
        newDataStoreItem[(storeItem as User).LoginID] = storeItem;
        break;
      }
    }

    if (publish) this.publishInMemoryList(newDataStoreItem, dataStoreItemType);
  }

  // This method is responsible for Updating the data in UI
  // This method could be expensive, please don't call it from a loop.
  private publishInMemoryList(
    newDataStoreItem: Dictionary<dcDataStoreItem>,
    dataStoreItemType: DataStoreItemType
  ) {
    this.dataStoreDic[dataStoreItemType] = newDataStoreItem;

    this.subjectDataArray[dataStoreItemType].next(
      Object.assign({}, this.dataStoreDic[dataStoreItemType])
    );
  }

  private getItemFromLocalDB(
    key: number | string,
    dataStoreItemType: DataStoreItemType
  ): PromiseLike<dcDataStoreItem> {
    this.ngf.storeName = this.getDBStoreName(dataStoreItemType);
    if (key !== null) return this.ngf.getItem<dcDataStoreItem>(key.toString());
  }

  private setItemInLocalDB(
    elementKey: string,
    element: dcDataStoreItem,
    dataStoreItemType: DataStoreItemType
  ) {
    this.ngf.storeName = this.getDBStoreName(dataStoreItemType);
    return this.ngf.setItem<dcDataStoreItem>(elementKey, element);
  }

  private populateRequest(
    tags: dcTag[],
    request: DataRequest,
    triggerDefaultRequest = false
  ): DataRequest[] {
    request.Tags = triggerDefaultRequest
      ? tags
      : tags?.length
      ? tags.filter(tag => tag != null)
      : [];
    if (!triggerDefaultRequest) {
      if (!request.Tags || (request.Tags && request.Tags.length < 1)) return null;
    }

    const requestCollection: DataRequest[] = [];
    request.ExternalParameter = this.getExternalParameterDates(request.ExternalParameter);

    requestCollection.push(request);

    return requestCollection;
  }

  private getExternalParameterDates(externalParameter: dcExternalParameter) {
    externalParameter = Object.assign({}, externalParameter);
    if (externalParameter && externalParameter.StartTime)
      externalParameter.StartTime = SharedCommonService.parseDateForApi(
        externalParameter.StartTime
      );
    if (externalParameter && externalParameter.EndTime)
      externalParameter.EndTime = SharedCommonService.parseDateForApi(externalParameter.EndTime);
    return externalParameter;
  }

  private mapDataStoreItemType(requestType: RequestType): DataStoreItemType {
    const requestedTypes = this.flaggedEnum.toArrayType<RequestType>(requestType, RequestType);

    if (
      requestedTypes[RequestType.Element] ||
      requestedTypes[RequestType.App] ||
      requestedTypes[RequestType.ProcessInstance]
    ) {
      return DataStoreItemType.Element;
    }

    if (requestedTypes[RequestType.Attribute]) {
      return DataStoreItemType.Attribute;
    }

    if (requestedTypes[RequestType.AttributeValue]) {
      return DataStoreItemType.AttributeValue;
    }

    if (requestedTypes[RequestType.AttributeTemplate]) {
      return DataStoreItemType.AttributeTemplate;
    }

    if (requestedTypes[RequestType.ElementTemplate]) {
      return DataStoreItemType.ElementTemplate;
    }

    if (requestedTypes[RequestType.DataReference]) {
      return DataStoreItemType.DataReference;
    }

    return +requestType;
  }

  private getDataStoreKey(tag: dcTag, dataStoreItemType: DataStoreItemType) {
    if (tag.dataStoreKey && tag.dataStoreKey.trim().length > 0) return tag.dataStoreKey.trim();
    if (tag.SourceObjectID && tag.SourceObjectID > 0) return tag.SourceObjectID.toString();
    return tag.ObjectID.toString();
    /*switch (dataStoreItemType) {
    case DataStoreItemType.Element: {
      return tag.ObjectVersionID;
    }
    case DataStoreItemType.Attribute:
    case DataStoreItemType.AttributeValue: {
      return this.generateDataStoreKey(;
    }
    case DataStoreItemType.AttributeTemplate: {
      newDataStoreItem[(storeItem as dcAttributeTemplate).ElementTemplateVersionID] = storeItem;
      break;
    }
    case DataStoreItemType.ElementTemplate: {
      newDataStoreItem[(storeItem as dcElementTemplate).Header.VersionID] = storeItem;
      break;
    }
  }*/
  }

  private getDBStoreName(dataStoreItemType: DataStoreItemType): string {
    const name = DataStoreItemType[dataStoreItemType];
    return `${name}Store`;
  }

  public addRecentSearchTagToUserProfile(tag: dcTag) {
    this.getItemFromLocalDB(this.authService.userId, DataStoreItemType.UserProfile).then(
      (userProfile: User) => {
        if (!userProfile) {
          userProfile = new User();
          userProfile.LoginID = this.authService.userId;
        }
        if (!userProfile.FrequentlySearched) userProfile.FrequentlySearched = [];
        const cnt = userProfile.FrequentlySearched.filter(
          frequentlySearched => frequentlySearched.TagName == tag.TagName
        );
        if (cnt.length == 0) {
          userProfile.FrequentlySearched.unshift(tag);
        }
        if (userProfile.FrequentlySearched.length > 10) {
          userProfile.FrequentlySearched.length = 10;
        }
        this.setUserProfile(userProfile);
      }
    );
  }

  public addUserMenuTagsToUserProfile(tags: dcTag[]) {
    this.getItemFromLocalDB(this.authService.userId, DataStoreItemType.UserProfile).then(
      (userProfile: User) => {
        if (!userProfile) {
          userProfile = new User();
          userProfile.LoginID = this.authService.userId;
        }
        if (!userProfile.UserMenuTags) userProfile.UserMenuTags = [];

        userProfile.UserMenuTags = tags;

        this.setUserProfile(userProfile);
      }
    );
  }

  public setUserProfile(userProfile: User) {
    const newDataStoreItem = this.dataStoreDic[DataStoreItemType.UserProfile];
    this.setItemInInMemoryArray(newDataStoreItem, userProfile, DataStoreItemType.UserProfile, true);

    return this.setItemInLocalDB(userProfile.LoginID, userProfile, DataStoreItemType.UserProfile);
  }

  public getUserProfile(): Observable<User> {
    const ret = this.Data[DataStoreItemType.UserProfile].pipe(
      map(dataStoreItems => {
        const element: any = dataStoreItems[this.authService.userId];
        if (!element && element !== undefined) {
          const promise = from(
            this.getItemFromLocalDB(this.authService.userId, DataStoreItemType.UserProfile)
          ).pipe(
            map((localDBElement: any) => {
              if (!localDBElement) {
                return null;
              }
              return localDBElement;
            })
          );

          return promise;
        }
        return of(element);
      }),
      mergeMap(a => a)
    ) as Observable<User>;
    return ret;
  }

  public getValue(value: dcValue, valueTypeId: ValueType) {
    return value.ValueString;
  }

  public getDCValues(element: dcElement, attributeName: string, dataMethod: DataMethods) {
    const appNameAttribute = element.Attributes.find(
      attribute => attribute.Header.AttributeTemplateName == attributeName
    );
    if (!appNameAttribute) return null;
    const appNameAttributeValue = element.AttributeValues.find(
      attributeValue => attributeValue.AttributeID == appNameAttribute.Header.AttributeID
    );
    const dataMethodKeyName = DataMethods[dataMethod];
    const appNameAttributeValueData = appNameAttributeValue
      ? (appNameAttributeValue.Data as any)
      : null;
    if (
      appNameAttributeValue &&
      appNameAttributeValueData &&
      appNameAttributeValueData[dataMethodKeyName] &&
      appNameAttributeValueData[dataMethodKeyName].length > 0
    ) {
      return {
        Values: appNameAttributeValueData[dataMethodKeyName],
        ValueTypeId: appNameAttributeValue.ValueTypeId
      };
    } else {
      const Values: dcValue[] = [];
      const _dcValue = new dcValue();
      _dcValue.ValueInt = appNameAttribute.Header.ValueInt;
      _dcValue.ValueDouble = appNameAttribute.Header.ValueDouble;
      _dcValue.ValueString = appNameAttribute.Header.ValueString;
      _dcValue.ValueTime = appNameAttribute.Header.ValueTime;
      _dcValue.ValueBoolean = appNameAttribute.Header.ValueBoolean;
      _dcValue.TimeStamp = appNameAttribute.Header.TimeStamp;
      _dcValue.IsGood = true;
      _dcValue.ActualType = appNameAttribute.Header.ValueTypeID;

      Values.push(_dcValue);
      return { Values, ValueTypeId: appNameAttribute.Header.ValueTypeID };
    }
    return null;
  }

  public getAttributeValue(
    element: dcElement,
    attributeName: string,
    dataMethod: DataMethods
  ): string | dcValue[] {
    const value = this.getDCValues(element, attributeName, dataMethod);
    if (value && value.Values && value.Values.length > 0) {
      if (dataMethod == DataMethods.Snapshot)
        return this.getValue(value.Values[0], value.ValueTypeId);
      return value.Values;
    }
    return [];
  }

  public getSnapshotValue(element: dcElement, attributeName: string) {
    if (element && element.Attributes) {
      const att = element.Attributes.find(
        a =>
          a &&
          a.Header &&
          a.Header.AttributeTemplateName &&
          a.Header.AttributeTemplateName.toUpperCase() == attributeName.toUpperCase()
      );
      if (att != undefined && att.Header) {
        return this.commonService.getValueFromAttribute(att, att.Header.ValueTypeID) as string;
      } else {
        return '';
      }
    } else {
      return '';
    }
    // return this.getAttributeValue(element, attributeName, DataMethods.Snapshot) as string;
  }

  // public getSnapshotValue(element:dcElement, attribute: dcAttribute) {
  //   return this.getAttributeValue(element, attributeName, DataMethods.Snapshot) as string;
  // }

  public getPlotValuesAttributeValue(element: dcElement, attributeName: string) {
    return this.getAttributeValue(element, attributeName, DataMethods.PlotValues);
  }

  requestForStateAppInfo(
    elementTemplateVersionId: number
  ): Observable<Dictionary<ProcessActivity[]>> {
    return this.httpService
      .post<DataResponse>('Process/GetAppInfoForState', elementTemplateVersionId)
      .pipe(
        map(data => {
          let mapData: Dictionary<ProcessActivity[]>;
          return mapData;
        }),
        catchError(() => {
          console.log('Could not create todo.');
          return EMPTY;
        })
      );
  }

  // This function will save the list of Smart Attributes
  public saveSmartAttributes(
    attributesToSave: AttributeSmartValue[],
    storedAttributeDataKeys: dcAttributeDataKey[],
    scopeTags: dcTag[]
  ) {
    const _dataKeyList: dcAttributeDataKey[] = [];
    const attributeValueList: Dictionary<dcValue[]> = {};
    const externalParameter = new dcExternalParameter({
      StartTime: null,
      EndTime: null,
      DataMethod: DataMethods.SetValues,
      Intervals: null,
      AggregateDataMethod: 0
    });
    if (attributesToSave && attributesToSave.length > 0) {
      attributesToSave.forEach(smartAttribute => {
        if (
          (smartAttribute.ValueTypeID != ValueType.String && smartAttribute.Value != '') ||
          smartAttribute.ValueTypeID == ValueType.String
        ) {
          const filterDataKey = storedAttributeDataKeys.filter(
            datakey =>
              datakey.AttributePath == smartAttribute.AttributeTemplatePath &&
              datakey.AttributeID == smartAttribute.AttributeID
          );

          if (filterDataKey && filterDataKey.length > 0) {
            const adk = filterDataKey[0].AttributeDataKey;
            const _dcValue = new dcValue();
            _dcValue.Value = smartAttribute.Value;
            switch (smartAttribute.ValueTypeID) {
              case ValueType.String:
                _dcValue.ValueString = smartAttribute.Value as string;
                break;
              case ValueType.DateTime:
                _dcValue.ValueTime = smartAttribute.Value as Date;
                break;
              case ValueType.Int32:
                _dcValue.ValueInt = +smartAttribute.Value;
                break;
              case ValueType.Double:
                _dcValue.ValueDouble = +smartAttribute.Value;
                break;
              default:
                _dcValue.ValueString = smartAttribute.Value as string;
                break;
            }
            _dcValue.ValueTypeID = smartAttribute.ValueTypeID;
            const date = new Date();
            date.setSeconds(0);
            date.setMilliseconds(0);
            _dcValue.TimeStamp = date;
            attributeValueList[adk] = [_dcValue];
            let _dataKeys: dcAttributeDataKey = new dcAttributeDataKey();
            _dataKeys = filterDataKey[0];
            _dataKeys.QueryOperation = QueryOperation.WRITE;
            _dataKeys.ExternalParameter = externalParameter;
            _dataKeys.ProcessInstanceID = -1;
            _dataKeys.ActivityInstanceID = -1;
            _dataKeyList.push(_dataKeys);
          }
        }
      });
    }

    if (!_dataKeyList.length) {
      return;
    }

    const dataReq: DataRequest = new DataRequest('', scopeTags, null, [], null);
    dataReq.AttributeDataKeys = _dataKeyList;
    dataReq.Data = attributeValueList;
    dataReq.Values = attributeValueList;
    dataReq.RequestType = RequestType.AttributeAndValuesAsync;
    dataReq.queryOperation = QueryOperation.WRITE;
    dataReq.ProcessInstanceID = -1;
    dataReq.ActivityInstanceID = -1;
    dataReq.ExternalParameter = externalParameter;

    this.sendRequest([dataReq], 1).subscribe(dataResponse => {
      if (!dataResponse) return;

      for (const dataStoreItemKey in dataResponse.Collection) {
        const dataStoreItem = dataResponse.Collection[dataStoreItemKey] as any;
        if (dataStoreItem.hasOwnProperty('Data') && Object.keys(dataStoreItem['Data']).length) {
          let errSave = false;
          for (const dataKey in dataStoreItem['Data']) {
            const value = dataStoreItem['Data'][dataKey];
            if (value && !value[0].IsGood) {
              errSave = true;
              this.toastService.openErrorToast(
                'Error saving data: ' + value[0].MessageString + ' for attribute: ' + dataKey
              );
            }
          }
          if (errSave) {
            return;
          }
          this.toastService.openInfoToast('Successfully saved data');
          return;
        } else {
          this.toastService.openErrorToast('Some error might have occured while saving data.');
          return;
        }
      }
    });
  }
}
